// src/components/CheckoutFormInner.tsx

import React, { useEffect, useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { StripeCardElement } from "@stripe/stripe-js";
import { getAppCurrencyType } from "../../Utils/Common";
import { getStripePaymmentIntent } from "../../Api/PaymentGateWay/PaymentGateWayApiService";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { PaymentIntent } from "@stripe/stripe-js";
import { Button } from "primereact/button";
import { Message } from 'primereact/message';

type FocusState = {
  number: boolean;
  expiry: boolean;
  cvc: boolean;
};

interface Props {
  amount: number;
  onClose: () => void;
  onPaymentComplete: (paymentId: PaymentIntent | undefined) => void;
}

const CheckoutFormInner: React.FC<Props> = ({ amount, onClose, onPaymentComplete }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [cardErrorMessage, setCardErrorMessage] = useState<string>("");
  const [csvErrorMessage, setCsvErrorMessage] = useState<string>("");
  const [expiryErrorMessage, setExpiryErrorMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const currencySymbol = getAppCurrencyType();

  const [focus, setFocus] = useState<FocusState>({
    number: false,
    expiry: false,
    cvc: false,
  });

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    if (!stripe || !elements) {
      setErrorMessage("Stripe has not loaded.");
      setLoading(false);
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);

    const cvc = elements.getElement(CardCvcElement);
    const expiry = elements.getElement(CardExpiryElement);

    if (!cardNumberElement) {
      setCardErrorMessage("Card number required.");
      setLoading(false);
      return;
    }

    if (!cvc) {
      setCsvErrorMessage("CVC code required.");
      setLoading(false);
      return;
    }

    if (!expiry) {
      setExpiryErrorMessage("Expirdy date required");
      setLoading(false);
      return;
    }

    // const cardElement = elements.getElement(CardElement) as StripeCardElement;

    // if (!cardElement) {
    //   setErrorMessage("Card Element not found.");
    //   setLoading(false);
    //   return;
    // }

    try {
      const response = await getStripePaymmentIntent(amount);
      const { clientSecret, error } = await response;

      if (error) {
        setErrorMessage(error);
        setLoading(false);
        return;
      }


      const paymentResult = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumberElement,

          //   csv:csv,
          //   expiry:expiry
        },
      });

      if (paymentResult.error) {
        setErrorMessage(paymentResult.error.message || "Payment failed.");
      } else if (paymentResult.paymentIntent?.status === "succeeded") {
        setPaymentSuccess(true);

        onPaymentComplete(paymentResult.paymentIntent);
        onClose();
      }

    } catch (err: any) {
      setErrorMessage(err.message || "Unexpected error.");
    }

    setLoading(false);
  };

  return (
    <div className="payment_form_area">
      <div className="payment_form_area_header">
        <h6>Enter your card details:</h6>
      </div>

      {!paymentSuccess ? (
        <form onSubmit={handleSubmit}>
          <div className="payment_form_area_body">

            <div className="row">
              <div className="col-12">
                <div className="page_form_group">
                  <label className="custom_form_label is_required">Card number</label>
                  <div className={`payment_input_field ${focus.number ? "focused" : ""}`}>
                    <CardNumberElement
                      className="payment_input"
                      options={{
                        style: {
                          base: {
                            fontSize: "15px",
                            color: "#424770",
                            "::placeholder": {
                              color: "#b9b9b9",
                            },
                          },
                          invalid: {
                            color: "#ff5757",
                          },
                        },
                        showIcon: true,

                      }}
                      onFocus={() => setFocus((prev) => ({ ...prev, number: true }))}
                      onBlur={() => setFocus((prev) => ({ ...prev, number: false }))}
                    />
                  </div>
                  <small className="form_error_msg" >{cardErrorMessage}</small>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="page_form_group">
                  <label className="custom_form_label is_required">Expiry</label>
                  <div className={`payment_input_field ${focus.expiry ? "focused" : ""}`}>
                    <CardExpiryElement
                      className="payment_input"
                      options={{
                        style: {
                          base: {
                            fontSize: "15px",
                            color: "#424770",
                            "::placeholder": {
                              color: "#b9b9b9",
                            },
                          },
                          invalid: {
                            color: "#ff5757",
                          },
                        },
                      }}
                      onFocus={() => setFocus((prev) => ({ ...prev, expiry: true }))}
                      onBlur={() => setFocus((prev) => ({ ...prev, expiry: false }))}
                    />
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="page_form_group">
                  <label className="custom_form_label is_required">CVC</label>
                  <div className={`payment_input_field ${focus.cvc ? "focused" : ""}`}>
                    <CardCvcElement
                      className="payment_input"
                      options={{
                        style: {
                          base: {
                            fontSize: "15px",
                            color: "#424770",
                            "::placeholder": {
                              color: "#b9b9b9",
                            },
                          },
                          invalid: {
                            color: "#ff5757",
                          },
                        },
                      }}
                      onFocus={() => setFocus((prev) => ({ ...prev, cvc: true }))}
                      onBlur={() => setFocus((prev) => ({ ...prev, cvc: false }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {errorMessage && (
            <Message
              severity="error"
              text={errorMessage}
              className="custom_form_msg mt-3"
            />
          )}

          <div className="form_action_area gap-2 mt-3">
            <Button
              label="Cancel"
              className="custom_btn secondary"
              onClick={onClose}
            />

            <Button
              type="submit"
              label={`${loading ? "Processing..." : `Pay ${currencySymbol} ${amount / 100}`}`}
              loading={loading}
              disabled={!stripe || loading}
              className="custom_btn primary me-0"
            />
          </div>
        </form>
      ) : (
        <Message
          severity="success"
          text="Payment Successful! 🎉"
          className="custom_form_msg mb-3"
        />
      )}
    </div>
  );
};

export default CheckoutFormInner;
